import { z } from 'zod';
/**
 * Assumption VALUE contracts (firm assumptions spec §4.2, §6.2, §6.3, §10.3).
 *
 * One expression schema used at every scope — set, plan, scenario, item — so a
 * rate typed into the firm editor and a rate typed as a plan override are
 * validated by the same code. There is deliberately no free-text formula kind:
 * the expression language is closed (literal, index + spread with collar, time
 * path), and anything else is refused rather than evaluated.
 *
 * Absent key = INHERIT. `{ kind: 'literal', valueBps: 0 }` = an explicit zero.
 * The two are different facts and nothing in this module converts one into the
 * other.
 */
/** Integer basis points. Non-integers are refused, never rounded. */
const intBps = z
    .number()
    .int('Rates are stored as a whole number of basis points')
    .refine(Number.isSafeInteger, 'Rate is outside the representable range');
/** Integer minor units (pence). */
const intMinor = z
    .number()
    .int('Money is stored as a whole number of pence')
    .refine(Number.isSafeInteger, 'Amount is outside the representable range');
export const assumptionCurrencySchema = z.literal('GBP');
/** A RATE or SHARE literal, e.g. 3.25% -> 325. */
export const literalRateValueSchema = z
    .object({ kind: z.literal('literal'), valueBps: intBps })
    .strict();
/** A MONEY literal in integer minor units. */
export const literalMoneyValueSchema = z
    .object({
    kind: z.literal('money'),
    amountMinor: intMinor,
    currency: assumptionCurrencySchema,
})
    .strict();
/** An AGE or COUNT literal; the definition declares what is being counted. */
export const literalCountValueSchema = z
    .object({ kind: z.literal('count'), count: z.number().int() })
    .strict();
/** A BOOL literal. `false` is a value; absence is inherit. */
export const literalBoolValueSchema = z
    .object({ kind: z.literal('bool'), on: z.boolean() })
    .strict();
/** An ENUM literal: a stable machine key from the definition's closed list. */
export const literalEnumValueSchema = z
    .object({ kind: z.literal('enum'), option: z.string().min(1).max(64) })
    .strict();
/**
 * A TEXT literal — a name a human reads and types, not a machine key
 * (§5.1 `indices.{id}.name` is "Text; REQ"). Trimmed and length-bounded here;
 * uniqueness and advice-wording are the collection's business, not the value's.
 */
export const literalTextValueSchema = z
    .object({ kind: z.literal('text'), text: z.string().trim().min(1).max(60) })
    .strict();
/**
 * A typed REFERENCE to another registered key (§4.2 REF). Used by an index
 * whose base is another index: the target is named as a registry KEY
 * (`indices.<id>`), never free text, so the reference graph is walkable and a
 * dangling target is a validation error rather than a silent zero.
 */
export const refValueSchema = z
    .object({ kind: z.literal('ref'), ref: z.string().min(1).max(160) })
    .strict();
/**
 * Index + spread with optional collar: min(cap, max(floor, index + spread)).
 * `indexKey` is another REGISTERED rate key (e.g. `economy.cpi`) — never text
 * to be evaluated.
 */
export const indexPlusValueSchema = z
    .object({
    kind: z.literal('indexPlus'),
    indexKey: z.string().min(1).max(120),
    spreadBps: intBps,
    floorBps: intBps.optional(),
    capBps: intBps.optional(),
})
    .strict()
    .refine((v) => v.floorBps === undefined || v.capBps === undefined || v.capBps >= v.floorBps, {
    message: 'The cap must be at least the floor',
    path: ['capBps'],
});
/** What a path segment may hold: a literal rate or an index expression. */
const segmentExpressionSchema = z.union([literalRateValueSchema, indexPlusValueSchema]);
export const pathAnchorSchema = z.enum(['taxYearStart', 'projectionOffset', 'personAge']);
export const pathSegmentSchema = z
    .object({
    /** Inclusive start, in the path's anchor system. */
    from: z.number().int(),
    /** Exclusive end; null = the explicit open-ended final segment. */
    to: z.number().int().nullable(),
    value: segmentExpressionSchema,
})
    .strict()
    .refine((s) => s.to === null || s.to > s.from, {
    message: 'A segment must end after it starts',
    path: ['to'],
});
/** §10.4 operational limit. */
export const MAX_PATH_SEGMENTS_PER_FIELD = 200;
export const pathValueSchema = z
    .object({
    kind: z.literal('path'),
    anchor: pathAnchorSchema,
    /** Required for a personAge anchor; stable role, never a name. */
    personRole: z.enum(['client', 'partner']).optional(),
    segments: z.array(pathSegmentSchema).min(1).max(MAX_PATH_SEGMENTS_PER_FIELD),
    tailPolicy: z.enum(['holdLast', 'requireExtension']).default('holdLast'),
})
    .strict()
    .superRefine((p, ctx) => {
    if (p.anchor === 'personAge' && p.personRole === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['personRole'],
            message: 'An age-anchored path must say whose age it follows',
        });
    }
    // Sorted ascending, no overlap, no gap, open end only on the last segment.
    for (let i = 0; i < p.segments.length; i += 1) {
        const seg = p.segments[i];
        const isLast = i === p.segments.length - 1;
        if (seg.to === null && !isLast) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['segments', i, 'to'],
                message: 'Only the final segment may be open-ended',
            });
            continue;
        }
        if (isLast)
            continue;
        const next = p.segments[i + 1];
        if (seg.to !== null && next.from < seg.to) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['segments', i + 1, 'from'],
                message: 'This segment overlaps the one before it',
            });
        }
        else if (seg.to !== null && next.from > seg.to) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['segments', i + 1, 'from'],
                message: 'There is an uncovered period before this segment',
            });
        }
    }
});
export const assumptionValueSchema = z.union([
    literalRateValueSchema,
    literalMoneyValueSchema,
    literalCountValueSchema,
    literalBoolValueSchema,
    literalEnumValueSchema,
    literalTextValueSchema,
    refValueSchema,
    indexPlusValueSchema,
    pathValueSchema,
]);
/**
 * Locale-declared separators. en-GB only today; declared rather than guessed so
 * "1.234" is never read as one thousand two hundred and thirty-four.
 */
const LOCALES = { 'en-GB': { group: ',', point: '.' } };
/**
 * Split entered text into integer and fraction DIGIT STRINGS without passing
 * through a binary float. Returns null when the text is not a plain number
 * under the declared locale.
 */
function splitDigits(text, locale) {
    const { group, point } = LOCALES[locale];
    let s = text.trim();
    if (s === '')
        return null;
    let negative = false;
    if (s.startsWith('-') || s.startsWith('−')) {
        negative = true;
        s = s.slice(1);
    }
    else if (s.startsWith('+')) {
        s = s.slice(1);
    }
    const parts = s.split(point);
    if (parts.length > 2)
        return null;
    const rawWhole = parts[0] ?? '';
    const fraction = parts[1] ?? '';
    if (!/^\d*$/.test(fraction))
        return null;
    let whole = rawWhole;
    if (rawWhole.includes(group)) {
        // Grouping must be well-formed thousands: 1,234,567 — not 12,34.
        if (!/^\d{1,3}(,\d{3})+$/.test(rawWhole))
            return null;
        whole = rawWhole.split(group).join('');
    }
    if (!/^\d*$/.test(whole))
        return null;
    if (whole === '' && fraction === '')
        return null;
    return { negative, whole: whole === '' ? '0' : whole, fraction };
}
function scaled(text, places, locale) {
    if (text.trim() === '')
        return { ok: false, reason: 'empty' };
    const d = splitDigits(text, locale);
    if (d === null)
        return { ok: false, reason: 'format' };
    // More places than the unit holds is a precision error, NOT a rounding cue —
    // unless the surplus digits are all zero ("3.250" is exactly 3.25).
    const surplus = d.fraction.slice(places);
    if (/[1-9]/.test(surplus))
        return { ok: false, reason: 'precision' };
    const kept = d.fraction.slice(0, places).padEnd(places, '0');
    const digits = `${d.whole}${kept}`.replace(/^0+(?=\d)/, '');
    if (digits.length > 15)
        return { ok: false, reason: 'range' };
    const magnitude = Number(digits);
    if (!Number.isSafeInteger(magnitude))
        return { ok: false, reason: 'range' };
    return { ok: true, amount: d.negative && magnitude !== 0 ? -magnitude : magnitude };
}
/** "3.25" -> 325 bps. "3.257" -> precision error. */
export function parsePercentToBps(text, locale = 'en-GB') {
    return scaled(text.replace(/%\s*$/, ''), 2, locale);
}
/** "123.45" / "£1,234.50" -> integer pence, by digit manipulation only. */
export function parsePoundsToMinor(text, locale = 'en-GB') {
    return scaled(text.trim().replace(/^£\s*/, ''), 2, locale);
}
/** 325 -> "3.25". Display only; never feeds a calculation. */
export function formatBpsAsPercent(bps) {
    const sign = bps < 0 ? '-' : '';
    const abs = Math.abs(bps);
    const whole = Math.trunc(abs / 100);
    const frac = String(abs % 100).padStart(2, '0');
    return `${sign}${whole}.${frac}`;
}
//# sourceMappingURL=value.js.map